import { useId } from 'react';
import { useGT } from 'gt-react';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { parseMonthInput, type OverrideWindowGrain } from './overrideWindow';

// The window every procurement override carries: an optional start (blank =
// starts now), an end (the override lapses on its own) and/or a review date (it
// keeps applying but shows up as needing review). One of the latter two is
// mandatory, so no override can live forever unnoticed
// (docs/procurement/lead-time-overrides.md, decision 2). The start is not part
// of that rule: a row that never starts is not a row that lives forever.
// Day-grain overrides pick calendar days; a growth factor picks whole months
// (its bounds are UTC month boundaries), and the review date stays a day at
// either grain because it is advisory rather than a bound.
// Renders as grid cells so it slots into the parent form's grid.

type Translate = ReturnType<typeof useGT>;

export function overrideWindowIsComplete(
  endsAt: string,
  reviewAt: string,
): boolean {
  return endsAt !== '' || reviewAt !== '';
}

/** A month-grain bound that was entered but does not parse (text fallback in a browser without `type="month"`). */
function malformedMonth(grain: OverrideWindowGrain, value: string): boolean {
  return grain === 'month' && value !== '' && parseMonthInput(value) === null;
}

/** Everything wrong with the window, in the order the buyer should fix it. */
function overrideWindowProblems(
  gt: Translate,
  flags: { incomplete: boolean; orderInvalid: boolean; malformed: boolean },
): string[] {
  const problems: string[] = [];
  if (flags.incomplete) {
    problems.push(
      gt(
        'Set an end date or a review date. An override cannot be open-ended without a review.',
        { context: 'override window validation message' },
      ),
    );
  }
  if (flags.orderInvalid) {
    problems.push(
      gt('The window must end after it starts.', {
        context: 'override window validation message (end not after start)',
      }),
    );
  }
  if (flags.malformed) {
    problems.push(
      gt('Enter months as YYYY-MM.', {
        context:
          'override window validation message for a month typed into a text fallback',
      }),
    );
  }
  return problems;
}

// A start or end bound: a date input at day grain, a month input at month
// grain (with a placeholder for the browsers that fall back to text).
function BoundInput({
  id,
  label,
  grain,
  value,
  onChange,
  disabled,
  invalid,
}: {
  id: string;
  label: string;
  grain: OverrideWindowGrain;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  invalid: boolean;
}) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-gray-500">
        {label}
      </Label>
      <Input
        id={id}
        type={grain === 'month' ? 'month' : 'date'}
        placeholder={grain === 'month' ? 'YYYY-MM' : undefined}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={invalid || undefined}
      />
    </div>
  );
}

export function OverrideWindowFields({
  grain = 'day',
  startsAt,
  endsAt,
  reviewAt,
  onStartsAtChange,
  onEndsAtChange,
  onReviewAtChange,
  orderInvalid = false,
  disabled = false,
}: {
  /** Calendar days (default) or whole months for the start and end. */
  grain?: OverrideWindowGrain;
  /**
   * Local YYYY-MM-DD (day grain) or YYYY-MM (month grain), or '' to start now.
   * Pass this together with `onStartsAtChange` to show the field; omit both
   * and the form keeps its two-field shape and the server's "starts now"
   * default.
   */
  startsAt?: string;
  /** Same shape as `startsAt`, or '' for open-ended. Month grain: the last month covered. */
  endsAt: string;
  /** Local YYYY-MM-DD or '' for no review date. */
  reviewAt: string;
  onStartsAtChange?: (value: string) => void;
  onEndsAtChange: (value: string) => void;
  onReviewAtChange: (value: string) => void;
  /** The end does not fall after the start; the parent judges it on the resolved payload. */
  orderInvalid?: boolean;
  disabled?: boolean;
}) {
  const gt = useGT();
  const id = useId();
  const incomplete = !overrideWindowIsComplete(endsAt, reviewAt);
  const startsMalformed = malformedMonth(grain, startsAt ?? '');
  const endsMalformed = malformedMonth(grain, endsAt);
  const problems = overrideWindowProblems(gt, {
    incomplete,
    orderInvalid,
    malformed: startsMalformed || endsMalformed,
  });
  return (
    <>
      {startsAt !== undefined && onStartsAtChange ? (
        <BoundInput
          id={`${id}-starts`}
          label={gt('Starts', { context: 'override window start field' })}
          grain={grain}
          value={startsAt}
          onChange={onStartsAtChange}
          disabled={disabled}
          invalid={startsMalformed || orderInvalid}
        />
      ) : null}
      <BoundInput
        id={`${id}-ends`}
        label={gt('Ends', { context: 'override window end field' })}
        grain={grain}
        value={endsAt}
        onChange={onEndsAtChange}
        disabled={disabled}
        invalid={incomplete || endsMalformed || orderInvalid}
      />
      <div>
        <Label htmlFor={`${id}-review`} className="text-xs text-gray-500">
          {gt('Review by', { context: 'override window review date field' })}
        </Label>
        <Input
          id={`${id}-review`}
          type="date"
          value={reviewAt}
          disabled={disabled}
          onChange={(e) => onReviewAtChange(e.target.value)}
          aria-invalid={incomplete || undefined}
        />
      </div>
      {grain === 'month' ? (
        <p className="col-span-2 text-[10px] text-gray-400">
          {gt('Whole forecast months; the end month is covered.', {
            context: 'override window hint for a month-grain override',
          })}
        </p>
      ) : null}
      {problems.map((problem) => (
        <p key={problem} className="col-span-2 text-xs text-amber-700">
          {problem}
        </p>
      ))}
    </>
  );
}
